import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../state'
import {NotificationType} from '../state/state'

interface UseWebstoreSsoOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

/**
 * Custom hook for handling Webstore SSO redirect flow
 * Fetches SSO payload and submits to 3rd-party webstore in new tab
 * Handles error notifications and validation
 *
 * @example
 * const {submitSsoRedirect} = useWebstoreSso()
 * const handleClick = async () => {
 *   await submitSsoRedirect()
 * }
 */
export const useWebstoreSso = (options?: UseWebstoreSsoOptions) => {
  const {state, actions} = useAppState()
  const {t} = useTranslation(['common'])

  const submitSsoRedirect = useCallback(async () => {
    try {
      const {actionUrl, token} = await actions.getWebstoreSsoPayload()

      // Validate payload
      if (!actionUrl || !token) {
        throw new Error('Invalid SSO payload: missing actionUrl or token')
      }

      // Create and submit hidden form
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = actionUrl
      form.target = '_blank'
      form.style.display = 'none'

      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'token'
      tokenInput.value = token
      form.appendChild(tokenInput)

      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)

      options?.onSuccess?.()
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      console.error('Webstore SSO redirect failed:', err)

      actions.addNotification({
        id: `webstore-sso-error-${Date.now()}`,
        type: NotificationType.ERROR,
        title: t('common:webstore.openFailedTitle', 'Unable to open Webstore'),
        description: t('common:webstore.openFailedDescription', 'Please try again in a moment.'),
        visible: true,
        hideAfterDelay: 6000
      })

      options?.onError?.(err)
    }
  }, [actions, t, options])

  return {submitSsoRedirect}
}
